import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useSelector } from "react-redux";
import { useLocation, useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import Grid from "@mui/material/Grid";
import Box from "@mui/material/Box";
import { isToday, isFuture, format, addMinutes, parseISO, isValid } from "date-fns";
// @ts-ignore
import { useTheme } from "@mui/material/styles";
// actions
import { useActions } from "../../../../common/hooks/useActions";
import { swissTravelPassActionCreators } from "./swissTravelPassModule";
// components
import { TransportRowAction, TransportRowAction2Buttons } from "../../shared/transportRow";
import AppDatePickerCustom from "../../../../components/AppDatePickerCustom";
import AppButton from "../../../../components/ui/AppButton";
import SwissTravelPassPassengersForm from "./SwissTravelPassPassengersForm";
import SwissTravelPassChildrenWithParentCheckbox from "./SwissTravelPassChildrenWithParentCheckbox";
import SwissTravelPassOfferSelection from "./SwissTravelPassOfferSelection";
import AppFormInput from "../../../../components/form/AppFormInputs/AppFormInput";
import SwissTravelPassSummary from "./SwissTravelPassSummary";
import PaymentModal from "../../../../components/Payment/PaymentModal";
import AppBookingError from "../../../../components/AppBookingError";
import TransportProviderHeader from "../../../../components/TransportProviderContent/TransportProviderHeader";
// icons
import { dateIcon, serviceClassIcon, emailIcon } from "../../../../common/vector";
// hooks
import { useTransportLanguageRefresh } from "../../../../common/hooks/useTransportLanguageRefresh";
// helpers
import { checkForEmptyNamesCamelCase } from "../helpers/checkForEmptyNames";
import { isCountryOfResidenceValid } from "../helpers/isCountryOfResidenceValid";
import { isEmailInvalid } from "../../../../common/utils/helpers/isEmailInvalid";
import { navigateBackOrToMain } from "../../../../common/utils/helpers/navigateBackOrToMain";
import { hasSwissFamilyCardEligibleChild } from "./helpers/isSwissFamilyCardChildAge";
import { pushTransportBookMarketplaceContext } from "../../../../common/utils/analytics/pushTransportBookMarketplaceContext";
import {
  buildAirlineNavigationState,
  clearPersistedAirlineRouterState,
  persistAirlineFromNavigationState,
  resolveTransportAirlineApiContext
} from "../../../../common/utils/transport/airlineTransportContext";
import { useCookies } from "react-cookie";

export default function SwissTravelPass() {
  const theme = useTheme();
  const [cookies] = useCookies(["cookieAccepted"]);
  const location = useLocation();
  const locationRef = useRef(location);
  locationRef.current = location;
  const navigate = useNavigate();
  const { t } = useTranslation();
  const [searchParams, setSearchParams] = useSearchParams();
  const language = localStorage.getItem("i18nextLng") || "en";

  const [selectedDate, setSelectedDate] = useState(new Date());
  const [selectedClass, setSelectedClass] = useState(2);
  const [selectedTravelPassOffer, setSelectedTravelPassOffer]: any = useState(null);
  const [email, setEmail] = useState("");
  const [comment, setComment] = useState("");
  const [childrenWithParent, setChildrenWithParent] = useState(false);
  const [offerSelectionError, setOfferSelectionError] = useState(false);
  const quickSelectParam: string | null = searchParams.get("qs");

  useLayoutEffect(() => {
    persistAirlineFromNavigationState(location.state);
  }, [location.state]);

  const restoredNavState = useMemo(() => buildAirlineNavigationState(location.state), [location.state]);

  const transportAirlineContext = useMemo(() => resolveTransportAirlineApiContext(location.state), [location.state]);

  const airlineNav = useMemo(() => {
    const nav = (restoredNavState ?? location.state) as {
      meta?: { source?: string };
      shared?: {
        offer_code?: string;
        departureDate?: string;
        arrivalDestination?: {
          placeId?: string | null;
          formattedAddress?: { secondary_text?: string | null } | null;
        } | null;
        finalDestination?: {
          placeId?: string | null;
          description?: string | null;
        } | null;
      };
    } | null;
    if (nav?.meta?.source !== "airline") {
      return {
        isAirlineSource: false,
        airlineOfferCode: undefined as string | undefined,
        airlineDepartureDate: undefined as string | undefined,
        departureAddress: null as string | null,
        departureAddressId: null as string | null,
        destinationAddress: null as string | null,
        destinationAddressId: null as string | null
      };
    }
    return {
      isAirlineSource: true,
      airlineOfferCode: nav.shared?.offer_code,
      airlineDepartureDate: nav.shared?.departureDate,
      departureAddress: nav.shared?.arrivalDestination?.formattedAddress?.secondary_text ?? null,
      departureAddressId: nav.shared?.arrivalDestination?.placeId ?? null,
      destinationAddress: nav.shared?.finalDestination?.description ?? null,
      destinationAddressId: nav.shared?.finalDestination?.placeId ?? null
    };
  }, [location.state, restoredNavState]);

  const {
    isAirlineSource,
    airlineOfferCode,
    airlineDepartureDate,
    departureAddress,
    departureAddressId,
    destinationAddress,
    destinationAddressId
  } = airlineNav;

  // states
  const tenantInfo = useSelector((state: any) => state.tenant.tenantInfo);
  const platformType = useSelector((state: any) => state.global.platformType);
  const globalLoader = useSelector((state: any) => state.global.globalLoader);
  const travelPassOffers = useSelector((state: any) => state.swissTravelPass.travelPassOffers);
  const travelPassPrebookingInfoResponse = useSelector(
    (state: any) => state.swissTravelPass.travelPassPrebookingInfoResponse
  );
  const travelPassPassengerList = useSelector((state: any) => state.swissTravelPass.travelPassPassengerList);
  const showValidationErrors = useSelector((state: any) => state.swissTravelPass.showValidationErrors);
  const travelPassPrebookingError = useSelector((state: any) => state.swissTravelPass.travelPassPrebookingError);
  const swissTravelPassBookingError = useSelector((state: any) => state.swissTravelPass.swissTravelPassBookingError);
  const swissTravelPassBookingResponseData = useSelector(
    (state: any) => state.swissTravelPass.swissTravelPassBookingResponseData
  );
  const showSwissTravelPassPaymentModal = useSelector(
    (state: any) => state.swissTravelPass.showSwissTravelPassPaymentModal
  );

  // actions
  const resetsSwissTravelPassState = useActions(swissTravelPassActionCreators?.resetsSwissTravelPassStateAction, []);
  const getSwissTravelPassOffers = useActions(swissTravelPassActionCreators?.getSwissTravelPassOffersAction, []);
  const getPriceTravelPassPrebook = useActions(swissTravelPassActionCreators?.getPriceTravelPassPrebookAction, []);
  const updateShowValidationErrors = useActions(swissTravelPassActionCreators?.updateShowValidationErrorsAction, []);
  const toggleSwissTravelPassPaymentModal = useActions(
    swissTravelPassActionCreators?.toggleSwissTravelPassPaymentModalAction,
    []
  );
  const buySwissTravelPassTicket = useActions(swissTravelPassActionCreators?.buySwissTravelPassTicketAction, []);
  const toggleSwissTravelPassBookingError = useActions(
    swissTravelPassActionCreators?.toggleSwissTravelPassBookingErrorAction,
    []
  );

  const navigateStp = useCallback(
    (to: string, options?: { replace?: boolean }) => {
      navigate(to, { replace: options?.replace, state: buildAirlineNavigationState(location.state) });
    },
    [navigate, location.state]
  );

  useEffect(() => {
    return () => {
      resetsSwissTravelPassState();
      clearPersistedAirlineRouterState();
    };
  }, [resetsSwissTravelPassState]);

  useEffect(() => {
    // Airline extension: prefill "VALID FROM" (date) on initial load.
    // Hotel flow has no location.state, so this is a no-op and remains fully backward compatible.
    if (!airlineDepartureDate) {
      return;
    }

    const parsed = parseISO(airlineDepartureDate);
    if (!isValid(parsed)) {
      return;
    }

    setSelectedDate(parsed);
  }, [airlineDepartureDate]);

  useEffect(() => {
    // Airline extension: preselect offer by offer_code by reusing existing quick-select pathway.
    // This triggers the same handlers/state updates as manual selection.
    if (!airlineOfferCode || quickSelectParam) {
      return;
    }

    setSearchParams({ qs: airlineOfferCode }, { replace: true, state: buildAirlineNavigationState(location.state) });
  }, [airlineOfferCode, quickSelectParam, location.state, setSearchParams]);

  useEffect(() => {
    if (tenantInfo) {
      getSwissTravelPassOffers(transportAirlineContext);
    }
  }, [tenantInfo, getSwissTravelPassOffers, transportAirlineContext]);

  useTransportLanguageRefresh({
    language,
    navigate,
    pathSuffix: "/transport/swiss_travel_pass",
    getNavigationState: () => buildAirlineNavigationState(locationRef.current.state),
    iataCode: transportAirlineContext.mode === "airline" ? transportAirlineContext.iataCode : null
  });

  const handleSelectedTravelPassOffer = useCallback(
    (offer: any, offerCode: string) => {
      setSelectedTravelPassOffer(offer);
      setOfferSelectionError(false);
      navigateStp(`/${language}/transport/swiss_travel_pass?qs=${offerCode}`, { replace: true });
    },
    [language, navigateStp]
  );

  useEffect(() => {
    if (quickSelectParam) {
      const selectedOffer = travelPassOffers?.items?.find((offer: any): any => offer?.offer_code === quickSelectParam);
      setSelectedTravelPassOffer(selectedOffer);
      setOfferSelectionError(false);

      handleSelectedTravelPassOffer(selectedOffer, quickSelectParam);
    }
  }, [language, travelPassOffers, quickSelectParam, handleSelectedTravelPassOffer]);

  const getDisabledStyleForTicketsForm = () => {
    if (travelPassPrebookingInfoResponse) {
      return { opacity: 0.4, pointerEvents: "none" };
    }
  };

  const hasEligibleChildPassenger = useMemo(
    () => hasSwissFamilyCardEligibleChild(travelPassPassengerList, selectedDate),
    [travelPassPassengerList, selectedDate]
  );

  const showSwissFamilyCardCheckbox = hasEligibleChildPassenger && selectedTravelPassOffer?.has_family_card === true;

  useEffect(() => {
    if (!showSwissFamilyCardCheckbox) {
      setChildrenWithParent(false);
    }
  }, [showSwissFamilyCardCheckbox]);

  const renderPrebookErrorMsg = () => {
    if (!travelPassPrebookingError) {
      return null;
    }
    let errors: Array<any> = [];

    if (travelPassPrebookingError) {
      errors.push({
        errorMessage: travelPassPrebookingError?.message
      });
    }

    return errors?.map((error: any, i: number) => (
      <div key={i} className="sbb__error-msg">
        {error?.errorMessage ? error?.errorMessage : t("transport.prebookCustomError")}
      </div>
    ));
  };

  const handleSeePriceBtn = () => {
    updateShowValidationErrors(true);

    if (!selectedTravelPassOffer?.offer_code) {
      setOfferSelectionError(true);
      return;
    }
    setOfferSelectionError(false);

    const isDateInPast = !(isToday(selectedDate) || isFuture(selectedDate));
    const isEmailIncorrect = isEmailInvalid(email) || email?.length === 0;
    const hasInvalidPassengerNames = checkForEmptyNamesCamelCase(travelPassPassengerList);
    const hasInvalidCountryOfResidence = !isCountryOfResidenceValid(travelPassPassengerList);

    if (isDateInPast || isEmailIncorrect || hasInvalidPassengerNames || hasInvalidCountryOfResidence) {
      return;
    }

    // Added a 30-minute validation offset. Normally, prebooking requires the selected time
    //  to be at least 20+ minutes in the future, but since Swiss Travel Pass has no time picker we "hack" it this way.
    const selectedDatePlus30min = addMinutes(selectedDate, 30);

    getPriceTravelPassPrebook(
      {
        qualityOfService: selectedClass,
        direction: null,
        start_date: format(selectedDatePlus30min, "yyyy-MM-dd HH:mm"),
        offer_code: selectedTravelPassOffer?.offer_code,
        departureAddress,
        departureAddressId,
        destinationAddress,
        destinationAddressId,
        origin: null,
        destination: null,
        email,
        childrenWithParent: showSwissFamilyCardCheckbox ? childrenWithParent : null
      },
      transportAirlineContext
    );
  };

  const handleBuyTicketBtn = () => {
    pushTransportBookMarketplaceContext({
      cookieAccepted: cookies.cookieAccepted,
      platformType,
      tenantInfo,
      offerCode: selectedTravelPassOffer?.offer_code ?? null
    });

    buySwissTravelPassTicket(
      {
        selectedDate: Math.round(+new Date(selectedDate) / 1000), // get unix timestamp
        selectedTravelPassOffer,
        email,
        comment
      },
      navigate,
      transportAirlineContext
    );
  };

  return (
    <Grid
      className="sbb grid-box"
      item
      sx={{ flexDirection: "column", flex: 1, margin: 0, padding: 0 }}
      mt={{ xs: 1, sm: 3 }}
      mb={{ xs: 4, sm: 5 }}
    >
      {swissTravelPassBookingError ? (
        <AppBookingError
          dataAttribute="swiss-travel-pass-booking-error-button"
          onButtonClick={() => toggleSwissTravelPassBookingError(false)}
          title={t("bookingForm.error.title")}
          subtitle={t("bookingForm.error.message")}
        />
      ) : (
        <>
          <TransportProviderHeader
            title={travelPassOffers?.provider_title ?? travelPassOffers?.title}
            shortDescription={travelPassOffers?.provider_description}
            providerLogo={travelPassOffers?.provider_logo}
          />

          {travelPassOffers?.items?.length > 0 ? (
            <Grid container>
              <Grid
                item
                sx={{ display: "flex", flexDirection: "column", ...getDisabledStyleForTicketsForm() }}
                md={6}
                xs={12}
              >
                <TransportRowAction
                  label={t("transport.validFrom")}
                  icon={dateIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppDatePickerCustom
                        selectedDate={selectedDate}
                        setSelectedDate={date => date != null && setSelectedDate(date)}
                        minDate={new Date()}
                      />
                    </Grid>
                  }
                />
                <TransportRowAction
                  label={t("transport.travelOptions")}
                  icon={serviceClassIcon?.default}
                  actionComponent={
                    <TransportRowAction2Buttons
                      firstButtonLabel={t("transport.swissTravelPass.firstClassBtn")}
                      firstButtonHandle={() => setSelectedClass(1)}
                      secondButtonLabel={t("transport.swissTravelPass.secondClassBtn")}
                      secondButtonHandle={() => setSelectedClass(2)}
                      isFirstButtonActive={selectedClass === 1}
                    />
                  }
                />
                <TransportRowAction
                  isRequired
                  label={t("transport.emailAddress")}
                  icon={emailIcon?.default}
                  actionComponent={
                    <Grid item width={{ xs: 200, sm: 220, md: 260 }}>
                      <AppFormInput
                        name={"email"}
                        type="string"
                        value={email}
                        onChange={e => setEmail(e.target.value)}
                        error={showValidationErrors && (isEmailInvalid(email) || email?.length === 0)}
                      />
                    </Grid>
                  }
                />
              </Grid>
              <Grid
                item
                md={6}
                xs={12}
                mt={{ sm: 6, xs: 6, md: 0 }}
                sx={{ ...getDisabledStyleForTicketsForm() }}
                position={"relative"}
              >
                <div className="vertical-divider" />
                <Box pl={{ xs: 2, sm: 6 }} pr={{ xs: 2, sm: 4 }}>
                  {travelPassOffers?.items?.length > 0 && (
                    <SwissTravelPassOfferSelection
                      handleSelectedTravelPassOffer={handleSelectedTravelPassOffer}
                      quickSelectParam={quickSelectParam}
                      travelPassOffers={travelPassOffers?.items}
                      selectedTravelPassOffer={selectedTravelPassOffer}
                    />
                  )}

                  <SwissTravelPassPassengersForm />

                  {showSwissFamilyCardCheckbox && (
                    <SwissTravelPassChildrenWithParentCheckbox
                      checked={childrenWithParent}
                      onChange={setChildrenWithParent}
                    />
                  )}

                  {renderPrebookErrorMsg()}
                  {offerSelectionError && (
                    <div className="sbb__error-msg">{t("transport.swissTravelPass.offerRequired")}</div>
                  )}
                  <Grid
                    container={isAirlineSource}
                    item={!isAirlineSource}
                    xs={12}
                    sm={isAirlineSource ? undefined : 4}
                    sx={{
                      ...(isAirlineSource ? { display: "flex", alignItems: "stretch", gap: 2 } : {})
                    }}
                  >
                    {isAirlineSource && (
                      <Grid item sx={{ flex: 1 }}>
                        <AppButton
                          label={t("transport.goBack")}
                          onClick={() => navigateBackOrToMain(navigate, { language })}
                          disableRipple={true}
                          customBackgroundColor={theme.palette.primary.dark}
                          customHoverColor={theme.palette.primary.dark}
                          fontSize="14px"
                          dataAttribute="swiss-travel-pass-go-back-1"
                        />
                      </Grid>
                    )}

                    <Grid
                      item
                      sx={{
                        ...(isAirlineSource ? { flex: 1 } : {})
                      }}
                    >
                      <AppButton
                        label={t("transport.seePriceBtn")}
                        onClick={handleSeePriceBtn}
                        disableRipple={true}
                        customBackgroundColor={tenantInfo?.style?.button?.color}
                        customActiveColor={tenantInfo?.style?.button?.active}
                        customHoverColor={tenantInfo?.style?.button?.hover}
                        customClickColor={tenantInfo?.style?.button?.click}
                        fontSize="14px"
                        dataAttribute="swiss-travel-pass-see-price"
                      />
                    </Grid>
                  </Grid>
                </Box>
              </Grid>
              <Grid item xs={12} mt={{ xs: 4, sm: 6 }} ml={{ xs: 2, sm: 5, md: 6 }} mr={{ xs: 2, sm: 5, md: 6 }}>
                <SwissTravelPassSummary
                  onBuyTicket={handleBuyTicketBtn}
                  comment={comment}
                  setComment={setComment}
                  selectedTravelPassOffer={selectedTravelPassOffer}
                  cancellationPolicy={
                    selectedTravelPassOffer?.cancellation_policy || travelPassOffers?.cancellation_policy
                  }
                />
              </Grid>
            </Grid>
          ) : (
            !globalLoader && (
              <Grid item xs={12} pb={4}>
                <Box sx={{ fontSize: 18 }}>{t("transport.notAvailable")}</Box>
              </Grid>
            )
          )}
        </>
      )}
      {showSwissTravelPassPaymentModal && (
        <PaymentModal
          isOpen={showSwissTravelPassPaymentModal}
          bookingResponseData={swissTravelPassBookingResponseData}
          togglePaymentModal={toggleSwissTravelPassPaymentModal}
          redirectUrl="transport/swiss_travel_pass/confirmation"
        />
      )}
    </Grid>
  );
}
